import { SORT_DIRECTION, type SortDirection } from "@/constants/sort"
import { formatTagFilterParam, parseTagFilterParam } from "@/tags/values"
import { endOfDay, startOfDay } from "date-fns"

import {
  parseMetadataFilters,
  serializeMetadataFilters,
} from "@/lib/metadataFilterSerialization"
import {
  parseMetricValueFilters,
  serializeMetricValueFilters,
  type MetricValueFilter,
} from "@/lib/metricValueFilters"
import { type MetadataFilter } from "@/types/MetadataFilter"
import { toISODateString } from "@/utils/date/date-range-helpers"

const BARE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Parse a URL date param.
 * A bare `YYYY-MM-DD` value is expanded to the local start- or end-of-day
 * (matching the monitoring table's picker semantics) so a same-day range
 * doesn't collapse to a zero-width UTC-midnight window. Full ISO timestamps
 * are parsed as-is.
 */
function parseUrlDate(raw: string, edge: "start" | "end"): Date | undefined {
  if (BARE_DATE_RE.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number)
    const local = new Date(y, m - 1, d)
    if (Number.isNaN(local.getTime())) {
      return undefined
    }
    return edge === "start" ? startOfDay(local) : endOfDay(local)
  }
  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

/** Max runs included when opening multi-run report via filters. Change this to adjust the cap. */
export const MULTI_RUN_LIMIT = 50

/** Higher limit for monitoring reports since each run is a single simulation output. */
export const MULTI_RUN_MONITORING_LIMIT = 2000

export const MULTI_RUN_FILTER_PARAM_KEYS = [
  "simulation_output_ids",
  "simulations_only",
  "search",
  "status",
  "template",
  "persona",
  "agent",
  "metric",
  "test_set",
  "test_case",
  "tag",
  "mutation",
  "scheduled_job",
  "date_start",
  "date_end",
  "sort_column",
  "sort_direction",
  "is_monitoring",
  "metadata_filters",
  "metric_value_filters",
  "uploaded_metric_value_filters",
  "date_field",
] as const

function getQueryString(
  query: Record<string, string | string[] | undefined>,
  key: string,
): string | null {
  const v = query[key]
  if (v == null) {
    return null
  }
  const s = Array.isArray(v) ? v[0] : v
  return typeof s === "string" && s.trim() !== "" ? s.trim() : null
}

function getQueryArray(
  query: Record<string, string | string[] | undefined>,
  key: string,
): string[] {
  const v = query[key]
  if (v == null) {
    return []
  }
  if (Array.isArray(v)) {
    return v
      .filter((s): s is string => typeof s === "string" && s.trim() !== "")
      .map((s) => s.trim())
  }
  if (typeof v === "string" && v.trim() !== "") {
    return v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
  }
  return []
}

/** Input shape for runs table filtering (subset we use for multi-run). */
interface RunsTableFilterInput {
  limit: number
  cursor: number
  searchQuery?: string
  sortColumn?:
    | "createdAt"
    | "testSetName"
    | "createdBy"
    | "modelType"
    | "templateName"
    | "agentId"
    | "id"
  sortDirection?: SortDirection
  agentFilters?: string[]
  statusFilters?: string[]
  templateFilters?: string[]
  personaFilters?: string[]
  metricFilters?: string[]
  testSetFilters?: string[]
  testCaseFilters?: string[]
  tagFilters?: string[]
  mutationFilters?: string[]
  scheduledJobFilters?: string[]
  dateRange?: { start?: Date; end?: Date }
  isMonitoring?: boolean
  simulationsOnly?: boolean
  metadataFilters?: MetadataFilter[]
  metricValueFilters?: MetricValueFilter[]
  uploadedMetricValueFilters?: Array<{
    metricId: string
    operator: ">" | "<" | "=" | ">=" | "<="
    value: string
    valueType: "string" | "float"
  }>
  dateField?: "created_at" | "occurred_at"
}

function parseUploadedMetricValueFilters(raw: string | null) {
  if (!raw) {
    return undefined
  }

  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return undefined
    }
    const filters: unknown[] = parsed
    return filters.filter(
      (
        filter,
      ): filter is NonNullable<
        RunsTableFilterInput["uploadedMetricValueFilters"]
      >[number] => {
        if (typeof filter !== "object" || filter === null) {
          return false
        }

        const candidate = filter as Record<string, unknown>
        return (
          typeof candidate.metricId === "string" &&
          typeof candidate.value === "string" &&
          (candidate.operator === ">" ||
            candidate.operator === "<" ||
            candidate.operator === "=" ||
            candidate.operator === ">=" ||
            candidate.operator === "<=") &&
          (candidate.valueType === "string" || candidate.valueType === "float")
        )
      },
    )
  } catch {
    return undefined
  }
}

interface MultiRunUrlState {
  /** True when the source is Uploaded conversations, including explicit ID selections. */
  isMonitoring: boolean
  /** When set, use these run IDs (no filter fetch). */
  runIds: string[] | null
  /** Optional exact simulation subset within the resolved runs. */
  simulationOutputIds: string[] | null
  /** When runIds is null and this is set, fetch runs with these params. */
  filterInput: RunsTableFilterInput | null
  hasFilterParams: boolean
}

/**
 * Parse router.query into either run_ids or filter input for runsTableRows.
 * run_ids takes precedence when present.
 */
export function parseMultiRunUrlState(
  query: Record<string, string | string[] | undefined>,
): MultiRunUrlState {
  const simulationOutputIds = getQueryArray(query, "simulation_output_ids")
  const isMonitoringRaw = getQueryString(query, "is_monitoring")
  const runIdsRaw = getQueryString(query, "run_ids")
  if (runIdsRaw) {
    const runIds = runIdsRaw
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
    if (runIds.length > 0) {
      return {
        isMonitoring: isMonitoringRaw === "true",
        runIds,
        simulationOutputIds:
          simulationOutputIds.length > 0 ? simulationOutputIds : null,
        filterInput: null,
        hasFilterParams: false,
      }
    }
  }

  const search = getQueryString(query, "search")
  const status = getQueryArray(query, "status")
  const template = getQueryArray(query, "template")
  const persona = getQueryArray(query, "persona")
  const agent = getQueryArray(query, "agent")
  const metric = getQueryArray(query, "metric")
  const testSet = getQueryArray(query, "test_set")
  const testCase = getQueryArray(query, "test_case")
  const tag = parseTagFilterParam(query.tag)
  const mutation = getQueryArray(query, "mutation")
  const scheduledJob = getQueryArray(query, "scheduled_job")
  const dateStart = getQueryString(query, "date_start")
  const dateEnd = getQueryString(query, "date_end")
  const sortColumnRaw = getQueryString(query, "sort_column")
  const sortDirectionRaw = getQueryString(query, "sort_direction")
  const simulationsOnlyRaw = getQueryString(query, "simulations_only")
  const metadataFiltersRaw = getQueryString(query, "metadata_filters")
  const metricValueFiltersRaw = getQueryString(query, "metric_value_filters")
  const uploadedMetricValueFiltersRaw = getQueryString(
    query,
    "uploaded_metric_value_filters",
  )
  const dateFieldRaw = getQueryString(query, "date_field")

  const hasFilterParams =
    search != null ||
    status.length > 0 ||
    template.length > 0 ||
    persona.length > 0 ||
    agent.length > 0 ||
    metric.length > 0 ||
    testSet.length > 0 ||
    testCase.length > 0 ||
    tag.length > 0 ||
    mutation.length > 0 ||
    scheduledJob.length > 0 ||
    dateStart != null ||
    dateEnd != null ||
    sortColumnRaw != null ||
    sortDirectionRaw != null ||
    isMonitoringRaw != null ||
    simulationsOnlyRaw != null ||
    metadataFiltersRaw != null ||
    metricValueFiltersRaw != null ||
    uploadedMetricValueFiltersRaw != null ||
    dateFieldRaw != null

  if (!hasFilterParams) {
    return {
      isMonitoring: false,
      runIds: simulationOutputIds.length > 0 ? [] : null,
      simulationOutputIds: null,
      filterInput: null,
      hasFilterParams: false,
    }
  }

  const sortColumn = sortColumnRaw as RunsTableFilterInput["sortColumn"] | null
  const validSortColumns: RunsTableFilterInput["sortColumn"][] = [
    "createdAt",
    "testSetName",
    "createdBy",
    "modelType",
    "templateName",
    "agentId",
    "id",
  ]
  const sortDirection =
    sortDirectionRaw === SORT_DIRECTION.ASC ||
    sortDirectionRaw === SORT_DIRECTION.DESC
      ? sortDirectionRaw
      : undefined

  const startDate = dateStart ? parseUrlDate(dateStart, "start") : undefined
  const endDate = dateEnd ? parseUrlDate(dateEnd, "end") : undefined
  const metricValueFilters = metricValueFiltersRaw
    ? parseMetricValueFilters(metricValueFiltersRaw)
    : []
  const uploadedMetricValueFilters = parseUploadedMetricValueFilters(
    uploadedMetricValueFiltersRaw,
  )

  const filterInput: RunsTableFilterInput = {
    limit:
      isMonitoringRaw === "true" ? MULTI_RUN_MONITORING_LIMIT : MULTI_RUN_LIMIT,
    cursor: 0,
    ...(search && { searchQuery: search }),
    ...(validSortColumns.includes(sortColumn ?? "createdAt") && {
      sortColumn: sortColumn ?? "createdAt",
    }),
    ...(sortDirection && { sortDirection }),
    ...(agent.length > 0 && { agentFilters: agent }),
    ...(status.length > 0 && { statusFilters: status }),
    ...(template.length > 0 && { templateFilters: template }),
    ...(persona.length > 0 && { personaFilters: persona }),
    ...(metric.length > 0 && { metricFilters: metric }),
    ...(testSet.length > 0 && { testSetFilters: testSet }),
    ...(testCase.length > 0 && { testCaseFilters: testCase }),
    ...(tag.length > 0 && { tagFilters: tag }),
    ...(mutation.length > 0 && { mutationFilters: mutation }),
    ...(scheduledJob.length > 0 && { scheduledJobFilters: scheduledJob }),
    ...((startDate || endDate) && {
      dateRange: { start: startDate, end: endDate },
    }),
    ...(isMonitoringRaw === "true" && { isMonitoring: true }),
    ...(simulationsOnlyRaw === "true" && { simulationsOnly: true }),
    ...(metadataFiltersRaw && {
      metadataFilters: parseMetadataFilters(metadataFiltersRaw),
    }),
    ...(metricValueFilters?.length && { metricValueFilters }),
    ...(uploadedMetricValueFilters?.length && {
      uploadedMetricValueFilters,
    }),
    ...((dateFieldRaw === "created_at" || dateFieldRaw === "occurred_at") && {
      dateField: dateFieldRaw,
    }),
  }

  return {
    isMonitoring: isMonitoringRaw === "true",
    runIds: null,
    simulationOutputIds:
      simulationOutputIds.length > 0 ? simulationOutputIds : null,
    filterInput,
    hasFilterParams: true,
  }
}

/** Build query string for multi-run URL from filter state (same shape as RunsFilterContext / runs table). */
export function buildMultiRunFilterQuery(params: {
  searchQuery?: string
  statusFilters?: string[]
  templateFilters?: string[]
  personaFilters?: string[]
  agentFilters?: string[]
  metricFilters?: string[]
  testSetFilters?: string[]
  testCaseFilters?: string[]
  tagFilters?: string[]
  mutationFilters?: string[]
  scheduledJobFilters?: string[]
  dateRange?: { start: Date | null; end: Date | null }
  sortColumn?: string
  sortDirection?: SortDirection
  isMonitoring?: boolean
  simulationsOnly?: boolean
  simulationOutputIds?: string[]
  metadataFilters?: MetadataFilter[]
  metricValueFilters?: MetricValueFilter[]
  uploadedMetricValueFilters?: Array<{
    metricId: string
    operator: ">" | "<" | "=" | ">=" | "<="
    value: string
    valueType: "string" | "float"
  }>
  dateField?: "created_at" | "occurred_at"
}): Record<string, string> {
  const q: Record<string, string> = {}
  if (params.searchQuery?.trim()) {
    q.search = params.searchQuery.trim()
  }
  if (params.statusFilters?.length) {
    q.status = params.statusFilters.join(",")
  }
  if (params.templateFilters?.length) {
    q.template = params.templateFilters.join(",")
  }
  if (params.personaFilters?.length) {
    q.persona = params.personaFilters.join(",")
  }
  if (params.agentFilters?.length) {
    q.agent = params.agentFilters.join(",")
  }
  if (params.metricFilters?.length) {
    q.metric = params.metricFilters.join(",")
  }
  if (params.testSetFilters?.length) {
    q.test_set = params.testSetFilters.join(",")
  }
  if (params.testCaseFilters?.length) {
    q.test_case = params.testCaseFilters.join(",")
  }
  if (params.tagFilters?.length) {
    const tagParam = formatTagFilterParam(params.tagFilters)
    if (tagParam) {
      q.tag = tagParam
    }
  }
  if (params.mutationFilters?.length) {
    q.mutation = params.mutationFilters.join(",")
  }
  if (params.scheduledJobFilters?.length) {
    q.scheduled_job = params.scheduledJobFilters.join(",")
  }
  if (params.dateRange?.start) {
    q.date_start = toISODateString(params.dateRange.start)
  }
  if (params.dateRange?.end) {
    q.date_end = toISODateString(params.dateRange.end)
  }
  if (params.sortColumn) {
    q.sort_column = params.sortColumn
  }
  if (params.sortDirection) {
    q.sort_direction = params.sortDirection
  }
  if (params.isMonitoring) {
    q.is_monitoring = "true"
  }
  if (params.simulationsOnly) {
    q.simulations_only = "true"
  }
  if (params.simulationOutputIds?.length) {
    q.simulation_output_ids = params.simulationOutputIds.join(",")
  }
  if (params.metadataFilters?.length) {
    q.metadata_filters = serializeMetadataFilters(params.metadataFilters)
  }
  if (params.metricValueFilters?.length) {
    q.metric_value_filters = serializeMetricValueFilters(
      params.metricValueFilters,
    )
  }
  if (params.uploadedMetricValueFilters?.length) {
    q.uploaded_metric_value_filters = JSON.stringify(
      params.uploadedMetricValueFilters,
    )
  }
  if (params.dateField) {
    q.date_field = params.dateField
  }
  return q
}

export function buildMultiRunUrlWithRunIds(
  basePath: string,
  runIds: string[],
  preserveParams?: Record<string, string>,
): string {
  const params = new URLSearchParams()
  if (runIds.length > 0) {
    params.set("run_ids", runIds.join(","))
  }
  if (preserveParams) {
    Object.entries(preserveParams).forEach(([k, v]) => {
      if (v != null && v !== "") {
        params.set(k, v)
      }
    })
  }
  const q = params.toString()
  return q ? `${basePath}?${q}` : basePath
}

export function buildMultiRunUrlWithFilters(
  basePath: string,
  filterQuery: Record<string, string>,
  preserveParams?: Record<string, string>,
): string {
  const params = new URLSearchParams()
  Object.entries(filterQuery).forEach(([k, v]) => {
    if (v != null && v !== "") {
      params.set(k, v)
    }
  })
  if (preserveParams) {
    Object.entries(preserveParams).forEach(([k, v]) => {
      if (v != null && v !== "") {
        params.set(k, v)
      }
    })
  }
  const q = params.toString()
  return q ? `${basePath}?${q}` : basePath
}
